import { useCallback } from "react";

import {
	arePinnedFiltersEqual,
	PinnedFilterTab,
	PinnedTabFilters,
} from "../utils/pinnedFilterTabs";

/** The pinned-tab mutations from {@link useListOverviewPinnedTabs}, which this hook narrates. */
interface PinnedTabMutations {
	pinnedTabs: PinnedFilterTab[];
	addPinnedTab: (name: string, filters: PinnedTabFilters) => Promise<unknown>;
	renamePinnedTab: (id: number, name: string) => Promise<unknown>;
	updatePinnedTabFilters: (id: number, filters: PinnedTabFilters) => Promise<unknown>;
	removePinnedTab: (id: number) => Promise<unknown>;
}

/**
 * Pin / update / rename / delete a saved filter tab and tell the user how it went.
 *
 * Extracted from `ListTable` (already over the 500-line ratchet), but it also earns its own file:
 * every one of these four is a mutation whose only feedback to the user is the toast written beside
 * it, so keeping them together is what makes "did the user hear about the failure?" a question with
 * one answer instead of four.
 */
export const useListPinnedTabToasts = ({
	mutations,
	currentFilters,
	notify,
	t,
}: {
	mutations: PinnedTabMutations;
	/** The filter state a "pin this view" / "update this pin" acts on. */
	currentFilters: PinnedTabFilters;
	notify: (message: string) => void;
	/** i18next `t`; the toast copy lives here rather than at the call site. */
	t: (key: string, options?: Record<string, unknown>) => string;
}) => {
	const { pinnedTabs, addPinnedTab, renamePinnedTab, updatePinnedTabFilters, removePinnedTab } =
		mutations;

	const handlePinCurrentView = useCallback(
		(name: string) => {
			addPinnedTab(name, currentFilters)
				.then(() => notify(t("list.pinTab.pinnedToast", { name })))
				.catch(() => notify(t("list.pinTab.errorToast")));
		},
		[addPinnedTab, currentFilters, notify, t]
	);

	const handleUpdatePinnedTab = useCallback(
		(id: number) => {
			const tab = pinnedTabs.find((pinned) => pinned.id === id);

			// Nothing to save, and no toast either: re-pinning an unchanged view is a no-op, not a
			// success worth announcing.
			if (!tab || arePinnedFiltersEqual(tab.filters, currentFilters)) {
				return;
			}

			updatePinnedTabFilters(id, currentFilters)
				.then(() => notify(t("list.pinTab.updatedToast", { name: tab.name })))
				.catch(() => notify(t("list.pinTab.errorToast")));
		},
		[pinnedTabs, currentFilters, updatePinnedTabFilters, notify, t]
	);

	const handleDeletePinnedTab = useCallback(
		(id: number) => {
			const tab = pinnedTabs.find((pinned) => pinned.id === id);

			removePinnedTab(id)
				.then(() => {
					if (tab) {
						notify(t("list.pinTab.deletedToast", { name: tab.name }));
					}
				})
				.catch(() => notify(t("list.pinTab.errorToast")));
		},
		[pinnedTabs, removePinnedTab, notify, t]
	);

	const handleRenamePinnedTab = useCallback(
		(id: number, name: string) => {
			renamePinnedTab(id, name).catch(() => notify(t("list.pinTab.errorToast")));
		},
		[renamePinnedTab, notify, t]
	);

	return {
		handlePinCurrentView,
		handleUpdatePinnedTab,
		handleDeletePinnedTab,
		handleRenamePinnedTab,
	};
};
